import {useQuery, type QueryKey} from '@github-ui/react-query'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  AnnotationLevel,
  ChangeType,
  type FileTreeResponse,
  FileTreeResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_tree'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {FileTreePayload} from '../payloads/file-tree'
import {buildDiffPathOrderMap} from '../../helpers/diff-helpers'
import {useLayoutStoreActions} from '../../stores/layout-selectors'

function fileTreeQueryKey(basePath: string): QueryKey {
  return [PageData.fileTree, basePath]
}

function fileTreeApiUrl(basePath: string): string {
  return `${basePath}/page_data/${PageData.fileTree}`
}

const FILE_TREE_RESPONSE_TYPE_NAME: FileTreeResponse['$typeName'] = 'github.pull_requests.v1.FileTreeResponse'

function isFileTreeResponse(response: FileTreeResponse | FileTreePayload): response is FileTreeResponse {
  return '$typeName' in response && response.$typeName === FILE_TREE_RESPONSE_TYPE_NAME
}

// Legacy `changeType` (PatchStatus) / `highestAnnotationLevel` strings -> proto enums.
const CHANGE_TYPE: Record<string, ChangeType> = {
  ADDED: ChangeType.ADDED,
  CHANGED: ChangeType.CHANGED,
  COPIED: ChangeType.COPIED,
  DELETED: ChangeType.DELETED,
  EMPTY: ChangeType.EMPTY,
  MODIFIED: ChangeType.MODIFIED,
  REMOVED: ChangeType.REMOVED,
  RENAMED: ChangeType.RENAMED,
  UNCHANGED: ChangeType.UNCHANGED,
}

const ANNOTATION_LEVEL: Record<string, AnnotationLevel> = {
  NOTICE: AnnotationLevel.NOTICE,
  WARNING: AnnotationLevel.WARNING,
  FAILURE: AnnotationLevel.FAILURE,
}

function markerToProto(marker: {id: number; start?: string; outdatedReason?: string}) {
  return {
    // int64 id -> bigint.
    id: BigInt(marker.id),
    start: marker.start ?? '',
    outdatedReason: marker.outdatedReason ?? '',
  }
}

function markerCollectionToProto(markers: {
  threads: Array<{id: number; start?: string; outdatedReason?: string}>
  annotations: Array<{id: number; start?: string; outdatedReason?: string}>
  ctx?: [number, number]
}) {
  return {
    threads: markers.threads.map(markerToProto),
    annotations: markers.annotations.map(markerToProto),
    ctx: markers.ctx ? [markers.ctx[0], markers.ctx[1]] : [],
  }
}

function markersMapToProto(markersMap: FileTreePayload['diffs'][number]['markersMap']) {
  const result: Record<string, ReturnType<typeof markerCollectionToProto>> = {}
  for (const [position, markers] of Object.entries(markersMap ?? {})) {
    result[position] = markerCollectionToProto(markers)
  }
  return result
}

/**
 * Temporary legacy (flag-off) JSON -> canonical proto shape. Ids become
 * `bigint`, literal enums become numeric proto enums, and absent/null values
 * become the proto3 defaults. This shim exists only while the `file_tree_proto`
 * backend flag rolls out and should be deleted once it is fully enabled.
 */
export function fileTreeToProto(data: FileTreePayload): FileTreeResponse {
  return create(FileTreeResponseSchema, {
    baseRefOid: data.baseRefOid,
    commits: data.commits.map(commit => ({
      actorLogin: commit.actorLogin,
      createdAt: commit.createdAt,
      messageHeadline: commit.messageHeadline,
      oid: commit.oid,
      shortOid: commit.shortOid,
    })),
    diffs: data.diffs.map(diff => ({
      changeType: CHANGE_TYPE[diff.changeType] ?? ChangeType.UNSPECIFIED,
      isCodeowner: diff.isCodeowner ?? false,
      isManifestFile: diff.isManifestFile ?? false,
      isVendored: diff.isVendored ?? false,
      linesAdded: diff.linesAdded,
      linesChanged: diff.linesChanged,
      linesDeleted: diff.linesDeleted,
      markedAsViewed: diff.markedAsViewed ?? false,
      path: diff.path,
      pathDigest: diff.pathDigest,
      highestAnnotationLevel: diff.highestAnnotationLevel
        ? (ANNOTATION_LEVEL[diff.highestAnnotationLevel] ?? AnnotationLevel.UNSPECIFIED)
        : AnnotationLevel.UNSPECIFIED,
      markersMap: markersMapToProto(diff.markersMap),
      isSymlink: diff.isSymlink ?? false,
    })),
    lastReviewOid: data.lastReviewOid ?? '',
    ownerLogin: data.ownerLogin,
    pathName: data.pathName,
    pullRequestId: data.pullRequestId,
    pullRequestNumber: data.pullRequestNumber,
    repositoryName: data.repositoryName,
  })
}

/**
 * Returns the canonical proto `FileTreeResponse`: an already-decoded proto
 * response (flag on, `$typeName` present) passes through untouched; a legacy
 * JSON response (flag off) is shimmed up to the proto shape.
 */
export function toFileTreeResponse(response: FileTreeResponse | FileTreePayload): FileTreeResponse {
  return isFileTreeResponse(response) ? response : fileTreeToProto(response)
}

/**
 * Lazily fetches the file_tree endpoint and hydrates the layout store with
 * the diff path order map. Intended for use when the comments side panel opens
 * on a page that doesn't have files-changed route data (e.g. conversation tab).
 */
export function useFileTreeDiffSummaries({basePath, enabled}: {basePath: string; enabled: boolean}) {
  const {hydrateDiffPathOrderMap} = useLayoutStoreActions()

  const query = useQuery<FileTreeResponse>({
    queryKey: fileTreeQueryKey(basePath),
    queryFn: async () => {
      const response = await queryFnFetch<typeof FileTreeResponseSchema, FileTreePayload>(
        {queryDeps: {pathname: fileTreeApiUrl(basePath)}, routeId: 'pull-requests', queryName: 'fileTree'},
        {schema: FileTreeResponseSchema},
      )
      const data = toFileTreeResponse(response)
      hydrateDiffPathOrderMap(buildDiffPathOrderMap(data.diffs))
      return data
    },
    enabled,
    staleTime: Infinity,
  })

  return query
}
